import React, { useContext, useState } from 'react'

import Tooltip from '@mui/material/Tooltip'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import { ButtonProps, CircularProgress } from '@mui/material'
import { toast } from 'react-toastify'

import Button from '../Common/Button'
import { removeDisplayFraisDePrepraProduct } from '@/lib/basket/rulesPreparationCosts'
import useAddProductBasket from '@/lib/hook/useAddProductBasket'
import ProfileContext from '../Context/ProfileContext'
import { recentAddToCart } from '@/lib/recentProducts'
import { gaAddToCart } from '@/lib/googleAnalytics/actions'
import logger from '@/lib/logger'
import { UsersOrdersProducts } from '@/src/types/api-types'

interface Props {
  buttonProps?: ButtonProps
  label?: string
  labelDisabled?: string
  oneOrder: {
    items: Pick<
      UsersOrdersProducts,
      'product_id' | 'amount' | 'available_to_cart' | 'shipped_amount' | 'product'
    >[]
  }
  variant?: 'icon' | 'text' | 'full'
}

const OrderButtonAddProductToBasket: React.FC<Props> = ({
  oneOrder,
  variant = 'text',
  label,
  labelDisabled,
  buttonProps = {},
}) => {
  const { addProductToBasket } = useAddProductBasket()
  const { refetchCounterProducts } = useContext(ProfileContext)
  // Loader pendant l'ajout : couvre la mutation ET le refetch du panier
  const [adding, setAdding] = useState(false)

  // Tracking best effort : une erreur d'analytics ne doit JAMAIS empecher l'ajout
  // au panier ni laisser le bouton bloque sur son loader.
  const trackAddToCart = (item: any): void => {
    try {
      recentAddToCart(item)

      gaAddToCart(item, item.amount)
    } catch (error) {
      logger.error(error)
    }
  }

  const handleAddProductBasket = async oneOrder => {
    if (adding) return
    setAdding(true)
    // `available_to_cart` ne conditionnait que l'etat du bouton, et seulement si TOUS les
    // articles etaient indisponibles (`every`) : une commande melangeant disponible et
    // indisponible renvoyait quand meme tout au panier. Les produits de programme (PLV,
    // sacherie) sont « caches » par construction — ils repartaient donc au panier hors
    // parcours PLV, sans rattachement a leur budget (constate le 14/09/2026).
    const orderable = oneOrder.items
      .filter(removeDisplayFraisDePrepraProduct)
      .filter(item => item.available_to_cart !== false)

    if (orderable.length === 0) {
      setAdding(false)
      toast.error('Ces produits ne sont plus disponibles à la commande.')
      return
    }

    const products = orderable.map(item => {
      trackAddToCart(item)

      return {
        product_id: String(item.product_id),
        product: item.product,
        amount: Number(item?.coeffQuantity ? item.coeffQuantity : item.amount),
      }
    })

    return addProductToBasket(products)
      .then(() => refetchCounterProducts())
      .catch(logger.error)
      .finally(() => setAdding(false))
  }

  const disabled = oneOrder?.items?.every(i => !i.available_to_cart)
  const _label = label || t({ id: 'web_ajouter_au_panier' })
  const _labelDisabled = labelDisabled

  const getButtonContent = () => {
    if (adding) return <CircularProgress size={20} color="inherit" />
    if (variant === 'text') return _label
    if (variant === 'icon') return <ShoppingCartIcon />

    return (
      <div className="flex items-center gap-2">
        <div>{_label}</div>
        <ShoppingCartIcon />
      </div>
    )
  }

  return (
    <Tooltip
      disableHoverListener={disabled && !_labelDisabled}
      title={disabled ? _labelDisabled : _label}
    >
      <span>
        <Button
          color={'secondary'}
          disabled={disabled || adding}
          onClick={() => handleAddProductBasket(oneOrder)}
          {...buttonProps}
        >
          {getButtonContent()}
        </Button>
      </span>
    </Tooltip>
  )
}

export default OrderButtonAddProductToBasket
